import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import * as authApi from '../../api/auth';
import { AuthShell } from './AuthShell';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input } from '../common/Input';
import { apiError } from '../../utils/apiError';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError(apiError(err, 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell subtitle="Reset your password">
      {submitted ? (
        <div className="text-center space-y-4">
          <svg className="w-12 h-12 mx-auto text-status-up" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700 dark:text-gray-300">
            If an account with that email exists, a reset link has been created.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No email service is configured, so the reset token is written to the server log.
          </p>
          <Link to="/login" className="block text-brand-600 dark:text-brand-400 hover:underline">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <Alert tone="error">{error}</Alert>}

          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter your email address and we'll create a password reset link.
          </p>

          <Field label="Email" required>
            {p => (
              <Input
                {...p}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            )}
          </Field>

          <Button type="submit" loading={loading} className="w-full" size="lg">
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">Back to sign in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
